import {Button} from "@zendeskgarden/react-buttons";
import {ReactElement, useMemo, useState} from "react";
import {userIsInternal} from "../../../authorization/AuthorizationCheckers";
import {AuthUser} from "../../../contexts/auth_types";
import {Account} from "../../../contexts/chartofaccounts/ChartOfAccountsContext";
import {MinimalProperty} from "../../../contexts/properties/logic";
import {ApiOperation} from "./api-mappings";
import {RenderAccountValueOverride} from "./renderers/account/RenderAccountValueOverride";
import {RenderDeleteAccountValueOverrides} from "./renderers/account/RenderDeleteAccountValueOverrides";
import {RenderUpsertAccountValues} from "./renderers/account/RenderUpsertAccountValues";
import {RenderActivateBudgetYear} from "./renderers/admin/RenderActivateBudgetYear";
import {RenderAddUpdateUnitType} from "./renderers/admin/RenderAddUpdateUnitType";
import {RenderCreateUpdateUser} from "./renderers/admin/RenderCreateUpdateUser";
import {RenderDeleteUnitType} from "./renderers/admin/RenderDeleteUnitType";
import {RenderDeleteUsers} from "./renderers/admin/RenderDeleteUsers";
import {RenderSetUnitTypePMSOverrides} from "./renderers/admin/RenderSetUnitTypePMSOverrides";
import {RenderUpdateManualProperty} from "./renderers/admin/RenderUpdateManualProperty";
import {RenderUpdateReforecastStartMonth} from "./renderers/admin/RenderUpdateReforecastStartMonth";
import {RenderBulkAddDrivers} from "./renderers/drivers/RenderBulkAddDrivers";
import {
    RenderBulkUpdateAccPercentageDriverAssumptions
} from "./renderers/drivers/RenderBulkUpdateAccPercentageDriverAssumptions";
import {RenderSetDrivers} from "./renderers/drivers/RenderSetDrivers";
import {
    RenderSetOperationalMonthlyAverageCustomIncreases
} from "./renderers/drivers/RenderSetOperationalMonthlyAverageCustomIncreases";
import {RenderSetPropertyDrivers} from "./renderers/drivers/RenderSetPropertyDrivers";
import {RenderSubmitCopyDrivers} from "./renderers/drivers/RenderSubmitCopyDrivers";
import {
    RenderUpdateAccPercentageDriverAssumptions
} from "./renderers/drivers/RenderUpdateAccPercentageDriverAssumptions";
import {RenderUpdateOperationalDriverAssumptions} from "./renderers/drivers/RenderUpdateOperationalDriverAssumptions";
import {RenderUpdateWorksheetLineAssumptions} from "./renderers/drivers/RenderUpdateWorksheetLineAssumptions";
import {RenderAuditLogsEnrichment} from "./renderers/RendererProps";
import {
    RenderCreateUpdateRenovationCostCategory
} from "./renderers/renovations/RenderCreateUpdateRenovationCostCategory";
import {RenderDeleteRenovationCostCategories} from "./renderers/renovations/RenderDeleteRenovationCostCategories";
import {RenderSetBudgetApproval} from "./renderers/reports/RenderSetBudgetApproval";
import {
    RenderSetOriginalRevenueMarketRentAdjustmentsGranular
} from "./renderers/revenue/RenderSetOriginalRevenueMarketRentAdjustmentsGranular";
import {RenderSetOriginalRevenueMarketRentMode} from "./renderers/revenue/RenderSetOriginalRevenueMarketRentMode";
import {
    RenderSetOriginalRevenueMarketRentOverridesGranular
} from "./renderers/revenue/RenderSetOriginalRevenueMarketRentOverridesGranular";
import {
    RenderSetOriginalRevenuePropertyRenewalIncreasePercent
} from "./renderers/revenue/RenderSetOriginalRevenuePropertyRenewalIncreasePercent";
import {RenderSubmitBulkSetAssumptions} from "./renderers/revenue/RenderSubmitBulkSetAssumptions";
import {RenderUpsertOpMetricValues} from "./renderers/revenue/RenderUpsertOpMetricValues";
import {RenderUpsertUnitTypeScheduleValues} from "./renderers/revenue/RenderUpsertUnitTypeScheduleValues";
import * as css from "./styles/RenderAuditLogVariables.module.scss";

export interface RenderAuditLogVariablesProps {
    user: AuthUser;
    operationName: string;
    variables: string;
    accounts: Account[];
    properties: MinimalProperty[];
    operationalMetricTypes: {id: string, name: string}[]
    enrichment?: RenderAuditLogsEnrichment;
}

export function RenderAuditLogVariables(props: RenderAuditLogVariablesProps): ReactElement {
    const [showJson, setShowJson] = useState<boolean>(false);

    const parsedVariables = useMemo(() => {
        try {
            const parsed = JSON.parse(props.variables);
            if(typeof parsed !== "object") {
                return null;
            }
            return parsed;
        } catch(e) {
            return null;
        }
    }, [props.variables]);

    if(parsedVariables === null) {
        return <></>;
    }

    let rendered = <></>;
    switch(props.operationName) {
        case ApiOperation.AccountValueOverride:
            rendered = <RenderAccountValueOverride user={props.user} variables={parsedVariables} />;
            break;
        case ApiOperation.UpdateWorksheetDriverData:
            rendered = <RenderUpdateWorksheetLineAssumptions user={props.user} variables={parsedVariables} enrichment={props.enrichment} />;
            break;
        case ApiOperation.ActivateBudgetYear:
            rendered = <RenderActivateBudgetYear user={props.user} variables={parsedVariables} />;
            break;
        case ApiOperation.AddUnitType:
            rendered = <RenderAddUpdateUnitType user={props.user} variables={parsedVariables} />;
            break;
        case ApiOperation.BulkAddDrivers:
            rendered = <RenderBulkAddDrivers
                accounts={props.accounts}
                operationalMetricTypes={props.operationalMetricTypes}
                user={props.user}
                variables={parsedVariables}
                enrichment={props.enrichment}
            />;
            break;
        case ApiOperation.BulkUpdateAccPercentageDriverAssumptions:
            rendered = <RenderBulkUpdateAccPercentageDriverAssumptions user={props.user} variables={parsedVariables} />;
            break;
        case ApiOperation.CreateUser:
            rendered = <RenderCreateUpdateUser properties={props.properties} user={props.user} variables={parsedVariables} enrichment={props.enrichment} />;
            break;
        case ApiOperation.DeleteAccountValueOverrides:
            rendered = <RenderDeleteAccountValueOverrides user={props.user} variables={parsedVariables} />;
            break;
        case ApiOperation.DeleteUnitType:
            rendered = <RenderDeleteUnitType user={props.user} variables={parsedVariables} />;
            break;
        case ApiOperation.DeleteUsers:
            rendered = <RenderDeleteUsers user={props.user} variables={parsedVariables} />;
            break;
        case ApiOperation.SetDrivers:
            rendered = <RenderSetDrivers
                user={props.user}
                variables={parsedVariables}
                accounts={props.accounts}
                operationalMetricTypes={props.operationalMetricTypes}
            />;
            break;
        case ApiOperation.SetOperationalMonthlyAverageCustomIncreases:
            rendered = <RenderSetOperationalMonthlyAverageCustomIncreases user={props.user} variables={parsedVariables} />;
            break;
        case ApiOperation.SetOriginalRevenueInPlaceRentOverrides:
            rendered = <RenderSetUnitTypePMSOverrides user={props.user} variables={parsedVariables} enrichment={props.enrichment} />;
            break;
        case ApiOperation.SetOriginalRevenueMarketRentAdjustmentsGranular:
            rendered = <RenderSetOriginalRevenueMarketRentAdjustmentsGranular
                user={props.user}
                variables={parsedVariables}
            />;
            break;
        case ApiOperation.SetOriginalRevenueMarketRentMode:
            rendered = <RenderSetOriginalRevenueMarketRentMode user={props.user} variables={parsedVariables} />;
            break;
        case ApiOperation.SetOriginalRevenueMarketRentOverridesGranular:
            rendered = <RenderSetOriginalRevenueMarketRentOverridesGranular
                user={props.user}
                variables={parsedVariables}
                enrichment={props.enrichment}
            />;
            break;
        case ApiOperation.SetOriginalRevenuePropertyRenewalIncreasePercent:
            rendered = <RenderSetOriginalRevenuePropertyRenewalIncreasePercent user={props.user} variables={parsedVariables} />;
            break;
        case ApiOperation.SetPropertyDrivers:
            rendered = <RenderSetPropertyDrivers
                accounts={props.accounts}
                operationalMetricTypes={props.operationalMetricTypes}
                user={props.user}
                variables={parsedVariables}
            />;
            break;
        case ApiOperation.SetUnitTypePMSOverrides:
            rendered = <RenderSetUnitTypePMSOverrides user={props.user} variables={parsedVariables} enrichment={props.enrichment} />;
            break;
        case ApiOperation.SubmitBulkSetAssumptions:
            rendered = <RenderSubmitBulkSetAssumptions properties={props.properties} user={props.user} variables={parsedVariables} />;
            break;
        case ApiOperation.SubmitCopyDrivers:
            rendered = <RenderSubmitCopyDrivers
                accounts={props.accounts}
                properties={props.properties}
                user={props.user}
                variables={parsedVariables}
            />;
            break;
        case ApiOperation.SetBudgetApproval:
            rendered = <RenderSetBudgetApproval variables={parsedVariables} />;
            break;
        case ApiOperation.UpdateAccPercentageDriverAssumptions:
            rendered = <RenderUpdateAccPercentageDriverAssumptions accounts={props.accounts} user={props.user} variables={parsedVariables} />;
            break;
        case ApiOperation.UpdateManualProperty:
            rendered = <RenderUpdateManualProperty user={props.user} variables={parsedVariables} />;
            break;
        case ApiOperation.UpdateOperationalDriverAssumptions:
            rendered = <RenderUpdateOperationalDriverAssumptions operationalMetricTypes={props.operationalMetricTypes} user={props.user} variables={parsedVariables} />;
            break;
        case ApiOperation.UpdateReforecastStartMonth:
            rendered = <RenderUpdateReforecastStartMonth user={props.user} variables={parsedVariables} />;
            break;
        case ApiOperation.UpdateUnitType:
            rendered = <RenderAddUpdateUnitType user={props.user} variables={parsedVariables} />;
            break;
        case ApiOperation.UpdateUser:
            rendered = <RenderCreateUpdateUser properties={props.properties} user={props.user} variables={parsedVariables} enrichment={props.enrichment} />;
            break;
        case ApiOperation.UpsertAccountValues:
            rendered = <RenderUpsertAccountValues user={props.user} variables={parsedVariables} />;
            break;
        case ApiOperation.UpsertOpMetricValues:
            rendered = <RenderUpsertOpMetricValues user={props.user} variables={parsedVariables} />;
            break;
        case ApiOperation.UpsertUnitTypeScheduleValues:
            rendered = <RenderUpsertUnitTypeScheduleValues user={props.user} variables={parsedVariables} />;
            break;
        case ApiOperation.CreateRenovationCostCategory:
        case ApiOperation.UpdateRenovationCostCategory:
            rendered = <RenderCreateUpdateRenovationCostCategory user={props.user} variables={parsedVariables} />;
            break;
        case ApiOperation.DeleteRenovationCostCategories:
            rendered = <RenderDeleteRenovationCostCategories user={props.user} variables={parsedVariables} />;
            break;
        default:
            if(userIsInternal(props.user)) {
                rendered = <pre>{JSON.stringify(parsedVariables, null, 2)}</pre>;
            }
            break;
    }

    if(userIsInternal(props.user)) {
        return <div className={css.renderAuditLogVariables}>
            {rendered}
            <div className={css.rawJson}>
                <div><Button isLink onClick={() => setShowJson(!showJson)}>{showJson ? "Hide" : "Show"} Json</Button></div>
                <div style={{display: showJson ? "block" : "none"}}>
                    <div>Raw Json:</div>
                    <pre>{JSON.stringify(parsedVariables, null, 2)}</pre>
                </div>
            </div>
        </div>;
    }
    return <div className={css.renderAuditLogVariables}>{rendered}</div>;
}